import { AfterViewInit, Component, ElementRef, model, signal, viewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ICellEditorAngularComp } from 'ag-grid-angular'
import type { ICellEditorParams } from 'ag-grid-community'
import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap'
import { MatIcon } from '@angular/material/icon'
import { TZDate } from '@date-fns/tz'
import { EditableRow } from './entity-metadata'

type ModelType = NgbDateStruct | undefined

const toDateStruct = (value: string): NgbDateStruct => {
  const tzDate = new TZDate(value, 'EST')
  return {
    year: tzDate.getFullYear(),
    month: tzDate.getMonth() + 1,
    day: tzDate.getDate(),
  }
}

export type DateCellEditorParams<T> = Readonly<{
  onSelect?: (row: EditableRow<T>, field: string, value: string | undefined) => void
}>


@Component({
  selector: 'div[app-date-cell-editor]',
  imports: [
    FormsModule,
    MatIcon,
    NgbInputDatepicker,
  ],
  styles: `
    :host {
      width: 100%;
      height: 100%;
      input {
        margin-top: 0;
        font-size: 14px;
      }
      .btn {
        border-radius: 0 6px 6px 0;
        padding: 5px 3px 0 3px;
        border-color: #c6c6c6;
      }
    }
  `,
  host: {
    class: 'input-group',
  },
  template: `
    <input #input ngbDatepicker
        class="form-control"
        [(ngModel)]="model"
        #d="ngbDatepicker"
    />
    <button (click)="d.toggle()" class='btn btn-outline-secondary'>
      <mat-icon fontIcon="calendar_month"></mat-icon>
    </button>
  `,
})
export class DateCellEditorComponent<T> implements ICellEditorAngularComp, AfterViewInit {
  protected readonly model = model<ModelType>()
  protected readonly params = signal<DateCellEditorParams<T>>({})
  protected readonly row = signal<EditableRow<T> | undefined>(undefined)
  protected readonly field = signal('')

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('input')

  constructor() {
    this.model.subscribe(() => {
      const { onSelect } = this.params()
      const row = this.row()
      if (onSelect && row) {
        onSelect(row, this.field(), this.getValue())
      }
    })
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const element = this.inputRef()?.nativeElement
      if (element) {
        element.focus()
        element.select()
      }
    }, 0)
  }

  agInit({ value, data, colDef }: ICellEditorParams<EditableRow<T>, string>) {
    this.params.set(colDef.cellEditorParams as DateCellEditorParams<T>)
    this.row.set(data)
    this.field.set(colDef.field as string)
    this.model.set(value == null || value.startsWith('9999') ? undefined : toDateStruct(value))
  }

  getValue(): string | undefined {
    const value = this.model()
    if (value) {
      return new Date(new TZDate(value.year, value.month - 1, value.day, 'EST')).toJSON()
    } else {
      return undefined
    }
  }
}